import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { Tag } from '../components/Tag';
import {
  Carregando,
  CampoBusca,
  ErroBox,
  PageHeader,
  Paginacao,
  Vazio,
} from '../components/ui';

/**
 * As pessoas que a plataforma conhece, com o que a conversa descobriu.
 *
 * ⚠️ **Lista GLOBAL, e é a única que pode ser.** A regra repetida em três
 * lugares deste código é que o que é de um evento mora dentro do evento — foi
 * por isso que Participantes virou aba e Recomendações mora em
 * `/eventos/:id/recomendacoes`. Mas o perfil é da PESSOA, não da participação
 * (decisão A7), e a pessoa é justamente a entidade que atravessa evento. Mesmo
 * motivo de `/agendamentos` e `/conversas` serem globais.
 *
 * Por que esta tela existe: até 12/10/2026 **não havia jeito nenhum de ler um
 * perfil no produto**. Onze blocos estruturados, a síntese, o perfil
 * psicológico e as notas ficavam gravados e nenhuma rota os devolvia — o único
 * acesso era `mysql` na VPS. E é esse texto que a fase 4 lê para justificar
 * conexões, o que significava justificar com um texto que nenhum humano jamais
 * tinha visto.
 */

interface PessoaNaLista {
  id: string;
  fullName: string;
  phoneE164: string;
  email: string | null;
  city: string | null;
  linkedin: string | null;
  optOutAt: string | null;
  profile: {
    synthesizedAt: string | null;
    lastUpdatedAt: string;
  } | null;
  /** A participação mais recente — é por ela que se abre o perfil. */
  participantId: string | null;
  jobTitle: string | null;
  company: string | null;
  evento: { id: string; name: string } | null;
  jornada: { status: string; currentStage: string | null } | null;
  totalDeEventos: number;
}

const ESTADOS_DA_JORNADA: Record<string, { rotulo: string; cor: string }> = {
  NAO_INICIADA: { rotulo: 'Não iniciada', cor: 'muted' },
  AGENDADA: { rotulo: 'Agendada', cor: 'info' },
  INICIANDO: { rotulo: 'Enviando convite', cor: 'info' },
  CONVITE_ENVIADO: { rotulo: 'Convite enviado', cor: 'info' },
  EM_ANDAMENTO: { rotulo: 'Em andamento', cor: 'info' },
  CONCLUIDA: { rotulo: 'Concluída', cor: 'success' },
  RECUSADA: { rotulo: 'Recusada', cor: 'muted' },
  ABANDONADA: { rotulo: 'Abandonada', cor: 'muted' },
  CANCELADA: { rotulo: 'Cancelada', cor: 'muted' },
  ERRO: { rotulo: 'Erro', cor: 'danger' },
  OPT_OUT: { rotulo: 'Pediu para sair', cor: 'muted' },
};

export function Perfis() {
  const navegar = useNavigate();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [soComPerfil, setSoComPerfil] = useState(false);
  const [eventoId, setEventoId] = useState('');

  const params = new URLSearchParams({ page: String(pagina), perPage: '20' });
  if (busca) params.set('search', busca);
  if (eventoId) params.set('eventId', eventoId);
  // Só manda quando está ligado: o servidor trata ausente como "mostrar todos",
  // e mandar `false` explícito seria a mesma coisa com mais bytes.
  if (soComPerfil) params.set('comPerfil', 'true');

  const consulta = useQuery({
    queryKey: ['pessoas', pagina, busca, eventoId, soComPerfil],
    queryFn: () => api<Paginado<PessoaNaLista>>(`/people?${params}`),
  });

  // Para o filtro por evento. Se falhar, o filtro simplesmente não aparece — a
  // lista é o que importa nesta tela.
  const eventos = useQuery({
    queryKey: ['eventos', 'para-filtro'],
    queryFn: () => api<Paginado<{ id: string; name: string }>>('/events?page=1&perPage=100'),
  });

  const reiniciar = <T,>(aplicar: (v: T) => void) => (valor: T) => {
    aplicar(valor);
    setPagina(1);
  };

  return (
    <>
      <PageHeader
        titulo="Participantes"
        subtitulo="O que a plataforma sabe sobre cada pessoa que já conversou, das mais completas para as menos. O perfil é da pessoa, não do evento — quem voltou numa segunda edição chega com o que a conversa anterior construiu."
      />

      <div className="barra-filtros">
        <CampoBusca
          valor={busca}
          aoMudar={reiniciar(setBusca)}
          placeholder="Buscar por nome, e-mail ou telefone…"
        />

        {eventos.data && eventos.data.data.length > 1 && (
          <select
            className="form-input"
            style={{ maxWidth: 240 }}
            value={eventoId}
            onChange={(e) => reiniciar(setEventoId)(e.target.value)}
          >
            <option value="">Todos os eventos</option>
            {eventos.data.data.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        )}

        <label className="filtro-inline">
          <input
            type="checkbox"
            checked={soComPerfil}
            onChange={(e) => reiniciar(setSoComPerfil)(e.target.checked)}
          />
          Só quem já conversou
        </label>
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-user-search"
            titulo={busca ? 'Ninguém encontrado' : 'Nenhum perfil ainda'}
            descricao={
              busca
                ? 'Tente outro termo de busca.'
                : /*
                     O mesmo recado que a tela de recomendações dá, e pelo mesmo
                     motivo: um vazio que não explica a causa deixa a pessoa
                     procurando um defeito onde não há nenhum.
                   */
                  'O perfil se forma na conversa da jornada. Enquanto ninguém tiver conversado, não há nada para ler aqui.'
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Evento</th>
                    <th>Jornada</th>
                    <th>Síntese</th>
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((p) => {
                    const estado = p.jornada
                      ? ESTADOS_DA_JORNADA[p.jornada.status]
                      : ESTADOS_DA_JORNADA.NAO_INICIADA;

                    return (
                      <tr
                        key={p.id}
                        className={p.participantId ? 'linha-clicavel' : undefined}
                        onClick={
                          p.participantId
                            ? () => navegar(`/participantes/${p.participantId}`)
                            : undefined
                        }
                      >
                        <td>
                          <div className="celula-principal">
                            {p.participantId ? (
                              <Link to={`/participantes/${p.participantId}`}>{p.fullName}</Link>
                            ) : (
                              p.fullName
                            )}
                            {/* Veterano de outra edição: é o que a decisão A7
                                compra, e sem a marca só a plataforma sabe. */}
                            {p.totalDeEventos > 1 && (
                              <Tag
                                type="info"
                                title={`Está em ${p.totalDeEventos} eventos desta organização`}
                                icon="ti-user-check"
                              >
                                já veio
                              </Tag>
                            )}
                            {p.optOutAt && (
                              <Tag
                                type="danger"
                                icon="ti-bell-off"
                                title="Pediu para não receber mais mensagens"
                              >
                                saiu
                              </Tag>
                            )}
                          </div>
                          {(p.jobTitle || p.company) && (
                            <div className="celula-secundaria">
                              {[p.jobTitle, p.company].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </td>

                        <td>
                          {p.evento ? (
                            p.evento.name
                          ) : (
                            <span style={{ color: 'var(--text-dim)' }}>—</span>
                          )}
                        </td>

                        <td>
                          <Tag type={estado?.cor ?? 'muted'}>
                            {estado?.rotulo ?? p.jornada?.status}
                          </Tag>
                        </td>

                        <td>
                          {/*
                            ⚠️ Três estados, e confundi-los é o erro fácil.

                            Sintetizado é o resumo do CONJUNTO. Sem síntese mas
                            com perfil é o resumo da última peça que chegou, que
                            pode ser uma resposta de uma palavra. Sem perfil
                            nenhum é ninguém que conversou.
                          */}
                          {p.profile?.synthesizedAt ? (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {new Date(p.profile.synthesizedAt).toLocaleDateString('pt-BR')}
                            </span>
                          ) : p.profile ? (
                            <Tag type="warning" title="O perfil existe, mas a síntese do conjunto ainda não rodou — o resumo é da última peça que chegou.">
                              parcial
                            </Tag>
                          ) : (
                            <span style={{ color: 'var(--text-dim)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}
    </>
  );
}
